import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import { UPDATE_AD_CAMPAIGN } from '@/graphql/ads/ad-campaigns-queries'
import { RECHECK_AD_SET_CHANGE } from '@/graphql/warmup-queries'
import { showNotification } from '@/utils/notifications'
import { maximumToMinor } from '@/utils/pixel-warmup'
import { describeBudgetGuardError } from '@/utils/pixel-warmup-panel'

const notify = (message, type = 'success', duration = 5000) =>
    showNotification(message, type, duration, null, null, true, true)

/**
 * What the owner can do about the maximum daily budget of one ad campaign, with or without a warm-up: change it and
 * check an unconfirmed change again. Both are manual and the server decides: a refusal is shown with its reason.
 * @param {string} adCampaignId
 * @param {{ refresh: () => Promise<void>, formatAmount?: (minor: number) => string }} options
 */
export const useCapPanel = (adCampaignId, { refresh, formatAmount }) => {
    const [busy, setBusy] = useState(false)
    // The code of the last refusal by the server, or null: the panel reacts to a feature that is not available
    const [refusalCode, setRefusalCode] = useState(null)
    const [updateMutation] = useMutation(UPDATE_AD_CAMPAIGN)
    const [recheckMutation] = useMutation(RECHECK_AD_SET_CHANGE)

    const guarded = useCallback(
        async (work, failure) => {
            setBusy(true)
            setRefusalCode(null)
            try {
                return await work()
            } catch (caught) {
                setRefusalCode(caught?.graphQLErrors?.[0]?.extensions?.code ?? null)
                notify(describeBudgetGuardError(caught, formatAmount) || caught?.message || failure, 'error', 10000)
                return false
            } finally {
                setBusy(false)
            }
        },
        [formatAmount],
    )

    /** Saves a new maximum; `typed` is in major units as the owner typed it. Returns whether it was saved. */
    const changeMaximum = useCallback(
        (typed) =>
            guarded(async () => {
                const minor = maximumToMinor(typed)
                if (minor === null) {
                    notify('Enter the maximum daily budget for the whole campaign.', 'error', 5000)
                    return false
                }
                await updateMutation({ variables: { adCampaignId, data: { max_daily_budget: minor } } })
                notify('The maximum daily budget was saved.')
                await refresh()
                return true
            }, 'The maximum daily budget could not be saved. Try again.'),
        [adCampaignId, guarded, refresh, updateMutation],
    )

    const recheck = useCallback(
        (adSetId) =>
            guarded(async () => {
                const { data } = await recheckMutation({ variables: { adSetId } })
                notify(data?.recheckAdSetChange?.message || 'Checked with Meta.', 'success', 8000)
                await refresh()
                return true
            }, 'Meta could not be checked right now. Try again.'),
        [guarded, recheckMutation, refresh],
    )

    return { busy, refusalCode, changeMaximum, recheck }
}
